// File: src/components/Admin/Billing.tsx
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import api from '../../services/api';
import errorLogger from '../../services/errorLogger.service';

const logger = errorLogger.createComponentLogger('Billing');

// Lazy load AutoRecharge component
const AutoRecharge = lazy(() => import('./AutoRecharge'));
const SMSUsageReport = lazy(() => import('./SMSUsageReport'));

interface CustomerBilling {
  customer: {
    id: number;
    companyName: string;
    firstName: string;
    lastName: string;
    email: string;
    status: 'Active' | 'Inactive';
  };
  package: {
    packageId: number;
    packageName: string;
    description: string;
    monthlyPrice: number;
    customPrice?: number;
    maxExtensions: number | null;
    maxDIDs: number | null;
    includedMinutes: number | null;
    extraMinuteRate: number;
    includesSMS: boolean;
    includesRecording: boolean;
    maxRecordingStorage?: number;
    additionalExtenRate?: number;
    additionalDIDRate?: number;
    softphoneRate?: number;
    softphoneIncluded?: boolean;
    smsIncluded?: number;
    smsRate?: number;
    mmsIncluded?: number;
    mmsRate?: number;
  } | null;
  usage: {
    extensionCount: number;
    didCount: number;
    softphoneCount: number;
    minutesUsed?: number;
    storageUsed?: string;
    smsUsed?: number;
    mmsUsed?: number;
  };
  rates?: Array<{
    r_id: number;
    service_name: string;
    qty: number;
    price: number;
    promoPrice?: number;
    promoEndDate?: string;
    billdate?: string;
  }>;
}

interface BillingHistoryEntry {
  id: number;
  custid: number;
  change_type: string;
  change_type_label: string;
  entity_type: string;
  entity_id: number;
  entity_name: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  description?: string;
  notes?: string;
  changed_by?: number;
  changed_by_name?: string;
  change_date: string;
  change_date_formatted: string;
  amount?: number;
  invoice_id?: number;
  balance_after?: number;
  subtotal?: number;
  is_prepayment?: boolean;
  prepaid_through?: string;
  tax_amount?: number;
}

interface PaymentMethod {
  id: number;
  lastFour: string;
  cardType: string;
  expiryMonth: string;
  expiryYear: string;
  isDefault: boolean;
  createdAt: string;
}

interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  subtotal: number;
  convenience_fee: number;
  credits_applied: number;
  total_amount: number;
  paid_amount: number;
  balance_due: number;
  status: string;
  aging_status: string;
  pdf_path?: string;
  items: Array<{
    item_type: string;
    description: string;
    quantity: number;
    unit_price: number;
    amount: number;
  }>;
  payments: Array<{
    id: number;
    payment_method: string;
    amount: number;
    payment_date: string;
    reference_number?: string;
  }>;
}

const Billing: React.FC = () => {
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();
  const customerFilter = useCustomerFilter();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState<CustomerBilling | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'usage' | 'history' | 'invoices' | 'payment' | 'autorecharge' | 'sms'>('usage');
  const [billingHistory, setBillingHistory] = useState<BillingHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showSMSReport, setShowSMSReport] = useState(false);
  const [billingCountry, setBillingCountry] = useState<'CA' | 'US'>('CA');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesSummary, setInvoicesSummary] = useState<{
    pending_count: number;
    paid_count: number;
    overdue_count: number;
    total_outstanding: number;
  }>({
    pending_count: 0,
    paid_count: 0,
    overdue_count: 0,
    total_outstanding: 0
  });
  
  // Check for login-as-customer token and session data
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = sessionStorage.getItem('loginAsCustomerId') && sessionStorage.getItem('loginAsCustomerName');
  
  // Initialize waiting state based on token presence
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  
  // Get the effective customer ID (check login as mode first, then filter, then logged-in user)
  const effectiveCustomerId = (() => {
    if (isLoginAsCustomer && member?.customerID) {
      return member.customerID.toString();
    } else if (customerFilter.customerId) {
      return customerFilter.customerId;
    } else {
      return member?.customerID?.toString() || null;
    }
  })();

  useEffect(() => {
    if (!waitingForLoginAs) {
      // Add small delay to ensure session storage is ready
      const timer = setTimeout(() => {
        fetchBillingData();
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCustomerId, waitingForLoginAs]);
  
  // Listen for login-as-customer changes
  useEffect(() => {
    const handleLoginAsChange = () => {
      logger.debug('Login-as-customer changed, refreshing data');
      fetchBillingData();
    };
    
    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange);
    return () => window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange);
  }, []);

  useEffect(() => {
    if (activeTab === 'history' && effectiveCustomerId) {
      fetchBillingHistory();
    } else if (activeTab === 'payment' && effectiveCustomerId) {
      fetchPaymentMethods();
    } else if (activeTab === 'invoices' && effectiveCustomerId) {
      fetchInvoices();
    }
  }, [activeTab, effectiveCustomerId]);

  // Update document title when managing a specific customer
  useEffect(() => {
    if (customerFilter.isFiltered && effectiveCustomerId && customerFilter.customerName) {
      const decodedCustomerName = decodeURIComponent(customerFilter.customerName);
      document.title = `${effectiveCustomerId} - ${decodedCustomerName} - Billing`;
    } else {
      document.title = 'Billing - Admin Portal';
    }
  }, [customerFilter.isFiltered, effectiveCustomerId, customerFilter.customerName]);

  const fetchBillingData = async () => {
    // Safety check - don't fetch if we're waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Skipping fetch - waiting for login-as data');
      return;
    }
    
    // Verify customer context matches if in login-as mode
    const sessionCustomerId = sessionStorage.getItem('loginAsCustomerId');
    if (sessionCustomerId && effectiveCustomerId && 
        sessionCustomerId !== String(effectiveCustomerId)) {
      logger.debug('Customer mismatch - waiting for correct context');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const params: any = {};
      
      if (effectiveCustomerId) {
        params.customerId = effectiveCustomerId;
      }
      
      logger.debug('Fetching billing data with params:', params);
      const response = await api.get('/endpoints/billing', { params });
      if (response.data.success) {
        setBillingData(response.data.data);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load billing information');
      logger.error('Billing error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getChangeTypeBadgeClass = (changeType: string): string => {
    switch (changeType) {
      case 'rate_added':
      case 'package_added':
      case 'promo_applied':
      case 'credit':
        return 'badge-success';
      case 'rate_removed':
      case 'package_removed':
      case 'promo_removed':
      case 'debit':
        return 'badge-danger';
      case 'price_changed':
      case 'quantity_changed':
      case 'rate_modified':
      case 'package_modified':
        return 'badge-warning';
      case 'add':
        return 'badge-info';
      case 'remove':
        return 'badge-secondary';
      default:
        return 'badge-secondary';
    }
  };

  const fetchBillingHistory = async () => {
    if (!effectiveCustomerId) return;
    
    logger.debug('Fetching transaction history for customer:', effectiveCustomerId);
    setHistoryLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('customerId', effectiveCustomerId.toString());
      
      const url = `/endpoints/transaction-history?${params.toString()}`;
      logger.debug('Calling URL:', url);
      
      const response = await api.get(url);
      logger.debug('Transaction history response:', response);
      
      if (response.data.success && response.data.data) {
        const responseData = response.data.data;
        logger.debug('Transaction history data:', responseData);
        
        if (responseData.history && Array.isArray(responseData.history)) {
          logger.debug(`Setting billing history with ${responseData.history.length} items`);
          setBillingHistory(responseData.history);
        } else {
          logger.debug('No history array found in response');
          setBillingHistory([]);
        }
      } else {
        logger.debug('Response not successful or no data');
        setBillingHistory([]);
      }
    } catch (err: any) {
      logger.error('Error fetching transaction history:', err);
      logger.error('Error response:', err.response);
      setBillingHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    if (!effectiveCustomerId) return;
    
    setPaymentLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('customerId', effectiveCustomerId.toString());
      params.append('path', 'payment-methods');
      
      const response = await api.get(`/endpoints/billing?${params.toString()}`);
      
      if (response.data.success && response.data.data) {
        setPaymentMethods(response.data.data);
      } else {
        setPaymentMethods([]);
      }
    } catch (err) {
      logger.error('Error fetching payment methods:', err);
      setPaymentMethods([]);
    } finally {
      setPaymentLoading(false);
    }
  };

  const fetchInvoices = async () => {
    if (!effectiveCustomerId) return;
    
    setInvoicesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('customerId', effectiveCustomerId.toString());
      params.append('path', 'invoices');
      params.append('limit', '50');
      
      const response = await api.get(`/endpoints/billing?${params.toString()}`);
      
      if (response.data.success && response.data.data) {
        setInvoices(response.data.data.invoices || []);
        if (response.data.data.summary) {
          setInvoicesSummary(response.data.data.summary);
        }
      } else {
        setInvoices([]);
      }
    } catch (err) {
      logger.error('Error fetching invoices:', err);
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleAddCard = async (cardData: any) => {
    if (!effectiveCustomerId) {
      alert('Please select a customer first');
      return;
    }
    
    try {
      const params = new URLSearchParams();
      params.append('customerId', effectiveCustomerId.toString());
      params.append('path', 'payment-methods');
      
      const response = await api.post(`/endpoints/billing?${params.toString()}`, cardData);
      
      if (response.data.success) {
        // Refresh payment methods
        fetchPaymentMethods();
        setShowAddCard(false);
        alert('Payment method added successfully!');
      } else {
        logger.error('API returned success=false:', response.data);
        alert(response.data.message || 'Failed to add payment method');
      }
    } catch (err: any) {
      logger.error('Error adding payment method:', err);
      logger.error('Error response:', err.response);
      
      // Better error handling
      let errorMessage = 'Failed to add payment method';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data) {
        errorMessage = typeof err.response.data === 'string' 
          ? err.response.data 
          : JSON.stringify(err.response.data);
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleViewInvoice = (invoiceId: number) => {
    // Get the access token
    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('Please login to view invoices');
      return;
    }
    
    // Open invoice in new window with token in URL (temporary token for viewing only)
    // API is on port 8080, not the React port
    const apiBaseUrl = process.env.REACT_APP_API_URL || '/api/v1';
    const invoiceUrl = `${apiBaseUrl}/endpoints/invoice-view-token?id=${invoiceId}&token=${encodeURIComponent(token)}`;
    logger.debug('Opening invoice URL:', invoiceUrl);
    const printWindow = window.open(invoiceUrl, '_blank', 'width=800,height=900,scrollbars=yes,resizable=yes');
    
    if (!printWindow) {
      alert('Please allow pop-ups to view invoices');
    }
  };

  const handleSetDefault = async (methodId: number) => {
    if (!effectiveCustomerId) {
      alert('Please select a customer first');
      return;
    }
    
    try {
      const params = new URLSearchParams();
      params.append('customerId', effectiveCustomerId.toString());
      params.append('path', `payment-methods/${methodId}/default`);
      
      const response = await api.put(`/endpoints/billing?${params.toString()}`);
      
      if (response.data.success) {
        fetchPaymentMethods();
      }
    } catch (err) {
      logger.error('Error setting default payment method:', err);
    }
  };

  const handleDeleteCard = async (methodId: number) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }
    
    if (!effectiveCustomerId) {
      alert('Please select a customer first');
      return;
    }
    
    try {
      const params = new URLSearchParams();
      params.append('customerId', effectiveCustomerId.toString());
      params.append('path', `payment-methods/${methodId}`);
      
      const response = await api.delete(`/endpoints/billing?${params.toString()}`);
      
      if (response.data.success) {
        fetchPaymentMethods();
      } else {
        alert(response.data.message || 'Failed to delete payment method');
      }
    } catch (err: any) {
      logger.error('Error deleting payment method:', err);
      alert(err.response?.data?.message || 'Failed to delete payment method');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <div style={{ color: '#dc3545' }}>{error}</div>
      </div>
    );
  }

  logger.debug('Rendering billing component, billingData:', billingData);
  
  if (!billingData) {
    logger.debug('No billing data - showing message');
    return (
      <div style={{ padding: '1.5rem' }}>
        <div style={{ color: '#ffc107' }}>No billing data available for this customer.</div>
      </div>
    );
  }

  const { customer, package: pkg, usage, rates = [] } = billingData;
  logger.debug('Destructured billing data', { customer, package: pkg, usage });
  
  // Check if customer has no package assigned
  if (!pkg) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <div className="billing-container">
          <div className="billing-header">
            <div>
              <h2>{customer.companyName || `${customer.firstName} ${customer.lastName}`}</h2>
              <div className="package-info">
                <span style={{ color: '#dc3545' }}>No active package assigned</span>
              </div>
            </div>
          </div>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
            <p>This customer does not have an active billing package.</p>
            <p>Please assign a package to enable billing features.</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Calculate values
  const effectivePrice = pkg?.customPrice && pkg.customPrice > 0 ? pkg.customPrice : (pkg?.monthlyPrice || 0);
  const additionalExtensions = Math.max(0, usage.extensionCount - (pkg?.maxExtensions || 0));
  const additionalDIDs = Math.max(0, usage.didCount - (pkg?.maxDIDs || 0));
  const includedMinutes = pkg?.includedMinutes || 0;
  const usedMinutes = usage.minutesUsed || 0;
  const extraMinutes = Math.max(0, usedMinutes - includedMinutes);
  
  // Default rates if not provided
  const additionalExtenRate = pkg?.additionalExtenRate || 14.95;
  const additionalDIDRate = pkg?.additionalDIDRate || 5.00;
  const softphoneRate = pkg?.softphoneRate || 5.00;
  const extraMinuteRate = pkg?.extraMinuteRate || 0.01;
  
  // Calculate SMS/MMS usage
  const smsIncluded = pkg?.smsIncluded || 0;
  const mmsIncluded = pkg?.mmsIncluded || 0;
  const smsUsed = usage.smsUsed || 0;
  const mmsUsed = usage.mmsUsed || 0;
  const extraSMS = Math.max(0, smsUsed - smsIncluded);
  const extraMMS = Math.max(0, mmsUsed - mmsIncluded);
  
  // Default SMS/MMS rates if not provided
  const smsRate = pkg?.smsRate || 0.01;
  const mmsRate = pkg?.mmsRate || 0.05;
  
  // Calculate costs
  const packageCost = effectivePrice;
  const addonExtensionsCost = additionalExtensions * additionalExtenRate;
  const addonDIDsCost = additionalDIDs * additionalDIDRate;
  // Only charge for softphones if they're not included in the package
  const softphoneCost = pkg?.softphoneIncluded ? 0 : (usage.softphoneCount * softphoneRate);
  const extraMinutesCost = extraMinutes * extraMinuteRate;
  const extraSMSCost = extraSMS * smsRate;
  const extraMMSCost = extraMMS * mmsRate;
  
  // Calculate customer rates total
  let customerRatesTotal = 0;
  rates.forEach(rate => {
    const usePromoPrice = rate.promoPrice && rate.promoPrice > 0 && 
      (!rate.promoEndDate || rate.promoEndDate === '0000-00-00' || new Date(rate.promoEndDate) > new Date());
    const price = usePromoPrice && rate.promoPrice ? rate.promoPrice : rate.price;
    customerRatesTotal += (price * rate.qty);
  });
  
  // Calculate total monthly
  const totalMonthly = packageCost + addonExtensionsCost + addonDIDsCost + softphoneCost + customerRatesTotal + extraMinutesCost + extraSMSCost + extraMMSCost;
  
  // Next billing date
  const nextBillingDate = new Date();
  nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
  nextBillingDate.setDate(1);

  const styles = `
    .page-title {
      margin-bottom: 2rem;
    }
    
    .billing-container {
      background-color: #fff;
      border-radius: 5px;
      box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    }
    
    .billing-header {
      padding: 1.5rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #ddd;
      flex-wrap: wrap;
    }
    
    .billing-header h2 {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 500;
    }
    
    .package-info {
      margin: 0.5rem 0;
    }
    
    .package-badge {
      display: inline-block;
      padding: 0.5rem 0.75rem;
      background-color: rgba(0, 102, 204, 0.1);
      color: #0066cc;
      border-radius: 50px;
      font-weight: 600;
      font-size: 0.9rem;
    }
    
    .billing-actions .btn {
      padding: 0.5rem 1rem;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 0.9rem;
    }
    
    .btn-primary {
      background-color: #0066cc;
      color: white;
    }
    
    .btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .monthly-total-bar {
      display: flex;
      align-items: center;
      padding: 1rem 1.5rem;
      background-color: #f8f9fa;
      border-bottom: 1px solid #ddd;
    }
    
    .monthly-label {
      font-size: 1rem;
      font-weight: 500;
      margin-right: 1rem;
    }
    
    .monthly-amount {
      font-size: 1.5rem;
      font-weight: 700;
      color: #28a745;
      margin-right: 2rem;
    }
    
    .monthly-date {
      font-size: 0.9rem;
      color: #6c757d;
    }
    
    .billing-summary {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1rem;
      padding: 1.5rem;
      background-color: #f8f9fa;
      border-bottom: 1px solid #ddd;
    }
    
    .summary-card {
      background-color: white;
      border-radius: 5px;
      box-shadow: 0 2px 5px rgba(0,0,0,0.05);
      padding: 1.25rem;
      display: flex;
      align-items: center;
    }
    
    .summary-icon {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background-color: rgba(0, 102, 204, 0.1);
      color: #0066cc;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      margin-right: 1rem;
    }
    
    .summary-content {
      flex: 1;
    }
    
    .summary-title {
      font-size: 0.9rem;
      color: #6c757d;
      margin-bottom: 0.25rem;
    }
    
    .summary-value {
      font-size: 1.5rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
    }
    
    .summary-price {
      font-size: 0.85rem;
      color: #6c757d;
    }

    .summary-breakdown {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      font-size: 0.85rem;
    }

    .breakdown-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.125rem 0;
    }

    .breakdown-item.included {
      color: #28a745;
    }

    .breakdown-item.billable {
      color: #dc3545;
      font-weight: 500;
    }

    .breakdown-label {
      margin-right: 0.5rem;
    }

    .breakdown-value {
      text-align: right;
      white-space: nowrap;
      font-family: monospace;
    }
    
    .billing-tabs {
      display: flex;
      border-bottom: 1px solid #ddd;
      overflow-x: auto;
    }
    
    .tab {
      padding: 1rem 2rem;
      cursor: pointer;
      font-weight: 500;
      transition: all 0.2s;
      white-space: nowrap;
      border: none;
      background: none;
    }
    
    .tab.active {
      border-bottom: 3px solid #0066cc;
      color: #0066cc;
    }
    
    .tab:hover:not(.active) {
      background-color: #f5f5f5;
    }
    
    .tab-content {
      padding: 1.5rem;
    }
    
    .billing-section {
      margin-bottom: 2rem;
    }
    
    .billing-section h3 {
      margin-top: 0;
      margin-bottom: 0.5rem;
      font-size: 1.25rem;
    }
    
    .billing-section p {
      color: #6c757d;
      margin-bottom: 1.5rem;
    }
    
    .billing-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 1rem;
    }
    
    .billing-table th, .billing-table td {
      padding: 0.75rem;
      border-bottom: 1px solid #dee2e6;
      text-align: left;
    }
    
    .billing-table th {
      font-weight: 600;
      background-color: #f8f9fa;
    }
    
    .billing-table tr:last-child td {
      border-bottom: none;
    }
    
    .total-row {
      background-color: #f8f9fa;
      font-weight: bold;
    }
    
    .promo-price {
      color: #28a745;
      font-weight: 600;
    }
    
    .original-price {
      text-decoration: line-through;
      color: #6c757d;
      font-size: 0.85rem;
      margin-left: 0.5rem;
    }
    
    .service-description {
      font-size: 0.85rem;
      color: #6c757d;
      margin-top: 0.25rem;
    }
    
    .usage-disclaimer {
      font-size: 0.85rem;
      color: #6c757d;
      font-style: italic;
      margin-top: 1rem;
    }
    
    .no-records {
      background-color: #f8f9fa;
      border-radius: 5px;
      padding: 2rem;
      text-align: center;
      color: #6c757d;
    }
    
    .no-records i {
      font-size: 2rem;
      margin-bottom: 1rem;
      color: #6c757d;
    }
    
    .status-badge {
      display: inline-block;
      padding: 0.25rem 0.5rem;
      border-radius: 50px;
      font-size: 0.75rem;
      font-weight: 600;
    }
    
    .status-paid {
      background-color: #d4edda;
      color: #155724;
    }
    
    .status-pending {
      background-color: #fff3cd;
      color: #856404;
    }
    
    .status-overdue {
      background-color: #f8d7da;
      color: #721c24;
    }
    
    .status-cancelled {
      background-color: #e2e3e5;
      color: #383d41;
    }
    
    .history-table {
      margin-top: 1rem;
    }

    .history-table table {
      width: 100%;
      border-collapse: collapse;
    }

    .history-table th,
    .history-table td {
      padding: 0.75rem;
      text-align: left;
      border-bottom: 1px solid #e9ecef;
    }

    .history-table th {
      background-color: #f8f9fa;
      font-weight: 600;
      color: #495057;
    }

    .badge {
      display: inline-block;
      padding: 0.25rem 0.5rem;
      font-size: 0.75rem;
      font-weight: 600;
      border-radius: 0.25rem;
      text-transform: uppercase;
    }

    .badge-success {
      background-color: #d4edda;
      color: #155724;
    }

    .badge-danger {
      background-color: #f8d7da;
      color: #721c24;
    }

    .badge-warning {
      background-color: #fff3cd;
      color: #856404;
    }

    .badge-secondary {
      background-color: #e2e3e5;
      color: #383d41;
    }

    .payment-methods {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .add-card-form {
      background-color: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 5px;
      padding: 1.5rem;
      margin-bottom: 2rem;
    }

    .add-card-form h4 {
      margin-top: 0;
      margin-bottom: 1rem;
      color: #333;
    }

    .form-group {
      margin-bottom: 1rem;
    }

    .form-group label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 600;
      color: #495057;
    }

    .form-control {
      width: 100%;
      padding: 0.5rem;
      border: 1px solid #ced4da;
      border-radius: 0.25rem;
      font-size: 1rem;
    }

    .form-row {
      display: flex;
      gap: 1rem;
      margin-bottom: 1rem;
    }

    .form-row .form-group {
      flex: 1;
    }

    .form-check {
      margin-bottom: 1rem;
    }

    .form-check-input {
      margin-right: 0.5rem;
    }

    .form-actions {
      margin-top: 1.5rem;
    }

    .btn {
      padding: 0.5rem 1rem;
      border: none;
      border-radius: 0.25rem;
      font-size: 1rem;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .btn-primary {
      background-color: #007bff;
      color: white;
    }

    .btn-primary:hover {
      background-color: #0056b3;
    }

    .btn-success {
      background-color: #28a745;
      color: white;
    }

    .btn-success:hover {
      background-color: #218838;
    }

    .btn-secondary {
      background-color: #6c757d;
      color: white;
    }

    .btn-secondary:hover {
      background-color: #5a6268;
    }
    
    .payment-card {
      display: flex;
      align-items: center;
      padding: 1.25rem;
      border: 1px solid #dee2e6;
      border-radius: 5px;
      background-color: #f8f9fa;
    }
    
    .card-type {
      font-size: 2rem;
      margin-right: 1.5rem;
    }
    
    .card-details {
      flex: 1;
    }
    
    .card-number {
      font-weight: 600;
      margin-bottom: 0.25rem;
    }
    
    .card-expiry {
      font-size: 0.85rem;
      color: #6c757d;
    }
    
    .action-button {
      padding: 0.25rem 0.75rem;
      margin-left: 0.5rem;
      border: 1px solid #dee2e6;
      background-color: white;
      border-radius: 4px;
      cursor: pointer;
      font-size: 0.85rem;
    }
    
    .action-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .action-button:hover:not(:disabled) {
      background-color: #f1f3f5;
    }
    
    .empty-state {
      text-align: center;
      padding: 3rem;
      background-color: #f8f9fa;
      border-radius: 8px;
    }
    
    .alert {
      padding: 1rem;
      border-radius: 4px;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    
    .alert-warning {
      background-color: #fff3cd;
      color: #856404;
      border: 1px solid #ffeaa7;
    }
  `;

  const formatDate = (date: Date): string => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  return (
    <>
      <style>{styles}</style>
      <h1 className="page-title">Billing Management</h1>
      
      <div className="billing-container">
        <div className="billing-header">
          <h2>Customer Billing Dashboard</h2>
          <div className="package-info">
            <span className="package-badge">
              <i className="fas fa-box"></i> Package: {pkg?.packageName || 'No Package'}
            </span>
          </div>
          <div className="billing-actions">
            <button className="btn btn-primary" disabled>
              <i className="fas fa-file-invoice-dollar"></i> Generate Invoice (Coming Soon)
            </button>
          </div>
        </div>
        
        <div className="monthly-total-bar">
          <div className="monthly-label">Total Monthly</div>
          <div className="monthly-amount">${totalMonthly.toFixed(2)}</div>
          <div className="monthly-date">Next billing: {formatDate(nextBillingDate)}</div>
        </div>
        
        <div className="billing-summary">
          <div className="summary-card">
            <div className="summary-icon">
              <i className="fas fa-phone-alt"></i>
            </div>
            <div className="summary-content">
              <div className="summary-title">Extensions</div>
              <div className="summary-value">{usage.extensionCount}</div>
              <div className="summary-breakdown">
                <div className="breakdown-item included">
                  <span className="breakdown-label">Included:</span>
                  <span className="breakdown-value">{pkg?.maxExtensions || 0}</span>
                </div>
                {additionalExtensions > 0 && (
                  <div className="breakdown-item billable">
                    <span className="breakdown-label">Billable:</span>
                    <span className="breakdown-value">{additionalExtensions} × ${additionalExtenRate.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-icon">
              <i className="fas fa-hashtag"></i>
            </div>
            <div className="summary-content">
              <div className="summary-title">Phone Numbers (DIDs)</div>
              <div className="summary-value">{usage.didCount}</div>
              <div className="summary-breakdown">
                <div className="breakdown-item included">
                  <span className="breakdown-label">Included:</span>
                  <span className="breakdown-value">{pkg?.maxDIDs || 0}</span>
                </div>
                {additionalDIDs > 0 && (
                  <div className="breakdown-item billable">
                    <span className="breakdown-label">Billable:</span>
                    <span className="breakdown-value">{additionalDIDs} × ${additionalDIDRate.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="summary-content">
              <div className="summary-title">Call Minutes</div>
              <div className="summary-value">{usedMinutes.toLocaleString()}</div>
              <div className="summary-breakdown">
                <div className="breakdown-item included">
                  <span className="breakdown-label">Included:</span>
                  <span className="breakdown-value">{includedMinutes.toLocaleString()}</span>
                </div>
                {extraMinutes > 0 && (
                  <div className="breakdown-item billable">
                    <span className="breakdown-label">Billable:</span>
                    <span className="breakdown-value">{extraMinutes.toLocaleString()} × ${extraMinuteRate.toFixed(4)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-icon">
              <i className="fas fa-mobile-alt"></i>
            </div>
            <div className="summary-content">
              <div className="summary-title">Softphones</div>
              <div className="summary-value">{usage.softphoneCount}</div>
              <div className="summary-breakdown">
                <div className="breakdown-item included">
                  <span className="breakdown-label">Included:</span>
                  <span className="breakdown-value">{pkg?.softphoneIncluded ? (pkg?.maxExtensions || 0) : '0'}</span>
                </div>
                {!pkg?.softphoneIncluded && usage.softphoneCount > 0 && (
                  <div className="breakdown-item billable">
                    <span className="breakdown-label">Billable:</span>
                    <span className="breakdown-value">{usage.softphoneCount} × ${softphoneRate.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">
              <i className="fas fa-comment-dots"></i>
            </div>
            <div className="summary-content">
              <div className="summary-title">SMS Messages</div>
              <div className="summary-value">{smsUsed.toLocaleString()}</div>
              <div className="summary-breakdown">
                <div className="breakdown-item included">
                  <span className="breakdown-label">Included:</span>
                  <span className="breakdown-value">{smsIncluded.toLocaleString()}</span>
                </div>
                {extraSMS > 0 && (
                  <div className="breakdown-item billable">
                    <span className="breakdown-label">Billable:</span>
                    <span className="breakdown-value">{extraSMS.toLocaleString()} × ${smsRate.toFixed(4)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">
              <i className="fas fa-image"></i>
            </div>
            <div className="summary-content">
              <div className="summary-title">MMS Messages</div>
              <div className="summary-value">{mmsUsed.toLocaleString()}</div>
              <div className="summary-breakdown">
                <div className="breakdown-item included">
                  <span className="breakdown-label">Included:</span>
                  <span className="breakdown-value">{mmsIncluded.toLocaleString()}</span>
                </div>
                {extraMMS > 0 && (
                  <div className="breakdown-item billable">
                    <span className="breakdown-label">Billable:</span>
                    <span className="breakdown-value">{extraMMS.toLocaleString()} × ${mmsRate.toFixed(4)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">
              <i className="fas fa-cloud-upload-alt"></i>
            </div>
            <div className="summary-content">
              <div className="summary-title">Recording Storage</div>
              <div className="summary-value">{usage.storageUsed || '0 MB'}</div>
              <div className="summary-breakdown">
                <div className="breakdown-item included">
                  <span className="breakdown-label">Included:</span>
                  <span className="breakdown-value">{pkg?.maxRecordingStorage || 0} GB</span>
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-label">Used:</span>
                  <span className="breakdown-value">{usage.storageUsed || '0 MB'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="billing-tabs">
          <button 
            className={`tab ${activeTab === 'usage' ? 'active' : ''}`}
            onClick={() => setActiveTab('usage')}
          >
            Usage Details
          </button>
          <button 
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Transaction History
          </button>
          <button 
            className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoices')}
          >
            Past Invoices
          </button>
          <button 
            className={`tab ${activeTab === 'payment' ? 'active' : ''}`}
            onClick={() => setActiveTab('payment')}
          >
            Payment Methods
          </button>
          <button 
            className={`tab ${activeTab === 'autorecharge' ? 'active' : ''}`}
            onClick={() => setActiveTab('autorecharge')}
          >
            Add Funds
          </button>
          <button 
            className={`tab ${activeTab === 'sms' ? 'active' : ''}`}
            onClick={() => setActiveTab('sms')}
          >
            SMS/MMS Usage
          </button>
        </div>
        
        {activeTab === 'usage' && (
          <div className="tab-content">
            <div className="billing-section">
              <h3>Package and Service Usage</h3>
              <p>Detailed breakdown of customer's package and additional services.</p>
              
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                    <th>Billing Period</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Base Package */}
                  {pkg && (
                    <tr>
                      <td>
                        {pkg.packageName} Package
                        {pkg.description && (
                          <div className="service-description">{pkg.description}</div>
                        )}
                      </td>
                      <td>1</td>
                      <td>
                        {pkg.customPrice && pkg.customPrice > 0 && pkg.customPrice !== pkg.monthlyPrice ? (
                          <>
                            <span className="promo-price">
                              ${pkg.customPrice.toFixed(2)}
                            </span>
                            <span className="original-price">
                              ${pkg.monthlyPrice.toFixed(2)}
                            </span>
                          </>
                        ) : (
                          `$${pkg.monthlyPrice.toFixed(2)}`
                        )}
                      </td>
                      <td>${packageCost.toFixed(2)}</td>
                      <td>Monthly</td>
                    </tr>
                  )}
                  
                  {/* Additional Extensions */}
                  {additionalExtensions > 0 && (
                    <tr>
                      <td>Additional Extension</td>
                      <td>{additionalExtensions}</td>
                      <td>${additionalExtenRate.toFixed(2)}</td>
                      <td>${addonExtensionsCost.toFixed(2)}</td>
                      <td>Monthly</td>
                    </tr>
                  )}
                  
                  {/* Additional DIDs */}
                  {additionalDIDs > 0 && (
                    <tr>
                      <td>Additional Phone Number</td>
                      <td>{additionalDIDs}</td>
                      <td>${additionalDIDRate.toFixed(2)}</td>
                      <td>${addonDIDsCost.toFixed(2)}</td>
                      <td>Monthly</td>
                    </tr>
                  )}
                  
                  {/* Softphones */}
                  {usage.softphoneCount > 0 && (
                    <tr>
                      <td>Softphone License{pkg?.softphoneIncluded && ' (Included)'}</td>
                      <td>{usage.softphoneCount}</td>
                      <td>${pkg?.softphoneIncluded ? '0.00' : softphoneRate.toFixed(2)}</td>
                      <td>${softphoneCost.toFixed(2)}</td>
                      <td>Monthly</td>
                    </tr>
                  )}
                  
                  {/* Extra Minutes */}
                  {extraMinutes > 0 && (
                    <tr>
                      <td>Additional Call Minutes</td>
                      <td>{extraMinutes.toLocaleString()}</td>
                      <td>${extraMinuteRate.toFixed(4)}</td>
                      <td>${extraMinutesCost.toFixed(2)}</td>
                      <td>Current Month</td>
                    </tr>
                  )}
                  
                  {/* Extra SMS Messages */}
                  {extraSMS > 0 && (
                    <tr>
                      <td>
                        Additional SMS Messages
                        <div className="service-description">
                          {smsUsed.toLocaleString()} used - {smsIncluded.toLocaleString()} included = {extraSMS.toLocaleString()} billable
                        </div>
                      </td>
                      <td>{extraSMS.toLocaleString()}</td>
                      <td>${smsRate.toFixed(4)}</td>
                      <td>${extraSMSCost.toFixed(2)}</td>
                      <td>Current Month</td>
                    </tr>
                  )}
                  
                  {/* MMS Messages - Show even if no overage */}
                  {mmsUsed > 0 && (
                    <tr>
                      <td>
                        {extraMMS > 0 ? 'Additional MMS Messages' : 'MMS Messages (Included)'}
                        <div className="service-description">
                          {mmsUsed.toLocaleString()} used - {mmsIncluded.toLocaleString()} included = {extraMMS.toLocaleString()} billable
                        </div>
                      </td>
                      <td>{extraMMS > 0 ? extraMMS.toLocaleString() : mmsUsed.toLocaleString()}</td>
                      <td>${extraMMS > 0 ? mmsRate.toFixed(4) : '0.0000'}</td>
                      <td>${extraMMSCost.toFixed(2)}</td>
                      <td>Current Month</td>
                    </tr>
                  )}
                  
                  {/* Customer Rates */}
                  {rates.map((rate, index) => {
                    const usePromoPrice = rate.promoPrice && rate.promoPrice > 0 && 
                      (!rate.promoEndDate || rate.promoEndDate === '0000-00-00' || new Date(rate.promoEndDate) > new Date());
                    const price = usePromoPrice && rate.promoPrice ? rate.promoPrice : rate.price;
                    
                    return (
                      <tr key={index}>
                        <td>{rate.service_name || 'Service (Description Missing)'}</td>
                        <td>{rate.qty}</td>
                        <td>
                          {usePromoPrice && rate.promoPrice ? (
                            <>
                              <span className="promo-price">
                                ${rate.promoPrice.toFixed(2)}
                              </span>
                              <span className="original-price">
                                ${rate.price.toFixed(2)}
                              </span>
                            </>
                          ) : (
                            `$${rate.price.toFixed(2)}`
                          )}
                        </td>
                        <td>${(price * rate.qty).toFixed(2)}</td>
                        <td>
                          {rate.billdate && rate.billdate !== '0000-00-00' 
                            ? formatDate(new Date(rate.billdate))
                            : 'Monthly'}
                        </td>
                      </tr>
                    );
                  })}
                  
                  <tr className="total-row">
                    <td colSpan={3}><strong>Monthly Total</strong></td>
                    <td><strong>${totalMonthly.toFixed(2)}</strong></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              
              <div className="usage-disclaimer">
                <p>This is a summary of the current usage. Actual billing may vary based on mid-month changes or additional services. Pro-rated charges may apply for features added or removed during the billing period. Rates are not inclusive of applicable taxes.</p>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'history' && (
          <div className="tab-content">
            <div className="billing-section">
              <h3>Transaction History</h3>
              <p>Recent changes to your account that may affect billing.</p>
              
              {historyLoading ? (
                <div className="text-center py-4">
                  <i className="fas fa-spinner fa-spin"></i> Loading history...
                </div>
              ) : billingHistory.length > 0 ? (
                <div className="history-table">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Service/Item</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Created By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingHistory.map((entry) => (
                        <tr key={entry.id}>
                          <td>{entry.change_date_formatted}</td>
                          <td>
                            <span className={`badge ${getChangeTypeBadgeClass(entry.change_type)}`}>
                              {entry.change_type === 'add' ? 'Service Added' :
                               entry.change_type === 'remove' ? 'Service Removed' :
                               entry.change_type_label || entry.change_type}
                            </span>
                            {entry.is_prepayment && (
                              <span className="badge badge-info" style={{ marginLeft: '4px' }}>
                                Prepaid
                              </span>
                            )}
                          </td>
                          <td>{entry.entity_name}</td>
                          <td>{entry.notes || entry.description || ''}</td>
                          <td>
                            {entry.amount !== undefined && entry.amount !== null ? (
                              <div>
                                <strong>${entry.amount.toFixed(2)}</strong>
                                {entry.subtotal !== undefined && entry.subtotal !== null && entry.tax_amount !== undefined && entry.tax_amount !== null && (
                                  <small className="text-muted" style={{ display: 'block', fontSize: '0.8em' }}>
                                    (Subtotal: ${entry.subtotal.toFixed(2)} + Tax: ${entry.tax_amount.toFixed(2)})
                                  </small>
                                )}
                                {entry.balance_after !== undefined && entry.balance_after !== null && (
                                  <small className="text-muted" style={{ display: 'block' }}>
                                    Balance: ${entry.balance_after.toFixed(2)}
                                  </small>
                                )}
                                {entry.is_prepayment && entry.prepaid_through && (
                                  <small className="text-info" style={{ display: 'block', marginTop: '4px' }}>
                                    <i className="fas fa-info-circle"></i> Prepaid through {new Date(entry.prepaid_through).toLocaleDateString()}
                                  </small>
                                )}
                              </div>
                            ) : entry.old_value && entry.new_value ? (
                              <small>
                                {entry.field_name}: {entry.old_value} → {entry.new_value}
                              </small>
                            ) : null}
                          </td>
                          <td>{entry.changed_by_name || 'System'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-records">
                  <i className="fas fa-info-circle"></i>
                  <p>No billing history changes have been recorded.</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'invoices' && (
          <div className="tab-content">
            <div className="billing-section">
              <h3>Past Invoices</h3>
              <p>History of invoices and payments for this account.</p>
              
              {invoicesSummary.total_outstanding > 0 && (
                <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
                  <i className="fas fa-exclamation-triangle"></i> Outstanding balance: ${invoicesSummary.total_outstanding.toFixed(2)}
                  {invoicesSummary.overdue_count > 0 && ` (${invoicesSummary.overdue_count} overdue)`}
                </div>
              )}
              
              {invoicesLoading ? (
                <div className="text-center py-4">
                  <i className="fas fa-spinner fa-spin"></i> Loading invoices...
                </div>
              ) : invoices.length > 0 ? (
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Date</th>
                      <th>Due Date</th>
                      <th>Amount</th>
                      <th>Paid</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => {
                      const formatDate = (dateStr: string) => {
                        const date = new Date(dateStr);
                        return date.toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        });
                      };
                      
                      const getStatusBadgeClass = (status: string, agingStatus: string) => {
                        if (status === 'paid') return 'status-paid';
                        if (status === 'cancelled') return 'status-cancelled';
                        if (agingStatus === 'overdue') return 'status-overdue';
                        if (agingStatus === 'due_soon') return 'status-pending';
                        return 'status-pending';
                      };
                      
                      const getStatusText = (status: string, agingStatus: string) => {
                        if (status === 'paid') return 'Paid';
                        if (status === 'cancelled') return 'Cancelled';
                        if (status === 'partially_paid') return 'Partial';
                        if (agingStatus === 'overdue') return 'Overdue';
                        if (agingStatus === 'due_soon') return 'Due Soon';
                        return 'Pending';
                      };
                      
                      return (
                        <tr key={invoice.id}>
                          <td>{invoice.invoice_number}</td>
                          <td>{formatDate(invoice.invoice_date)}</td>
                          <td>{formatDate(invoice.due_date)}</td>
                          <td>${invoice.total_amount.toFixed(2)}</td>
                          <td>${invoice.paid_amount.toFixed(2)}</td>
                          <td>${invoice.balance_due.toFixed(2)}</td>
                          <td>
                            <span className={`status-badge ${getStatusBadgeClass(invoice.status, invoice.aging_status)}`}>
                              {getStatusText(invoice.status, invoice.aging_status)}
                            </span>
                          </td>
                          <td>
                            <button 
                              className="action-button" 
                              onClick={() => handleViewInvoice(invoice.id)}
                              title="View Invoice"
                            >
                              <i className="fas fa-file-pdf"></i> View
                            </button>
                            {invoice.balance_due > 0 && (
                              <button 
                                className="action-button"
                                style={{ marginLeft: '0.5rem' }}
                                onClick={() => alert('Payment feature coming soon')}
                              >
                                <i className="fas fa-credit-card"></i> Pay
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">
                  <i className="fas fa-file-invoice" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                  <p style={{ marginTop: '1rem', color: '#666' }}>No invoices found for this customer.</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'payment' && (
          <div className="tab-content">
            <div className="billing-section">
              <h3>Payment Methods</h3>
              <p>Manage payment methods and billing preferences.</p>
              
              {effectiveCustomerId ? (
                <div className="mb-3">
                  <button 
                    className="btn btn-primary"
                    onClick={() => setShowAddCard(true)}
                    style={{ marginBottom: '1rem' }}
                  >
                    <i className="fas fa-plus"></i> Add New Card
                  </button>
                </div>
              ) : (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle"></i> Please select a customer to manage payment methods.
                </div>
              )}

              {showAddCard && (
                <div className="add-card-form">
                  <h4>Add New Payment Method</h4>
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target as HTMLFormElement);
                    handleAddCard({
                      cardholderName: formData.get('cardholderName'),
                      cardNumber: formData.get('cardNumber'),
                      expiryMonth: formData.get('expiryMonth'),
                      expiryYear: formData.get('expiryYear'),
                      cvv: formData.get('cvv'),
                      billingStreet: formData.get('billingStreet'),
                      billingCity: formData.get('billingCity'),
                      billingState: formData.get('billingState'),
                      billingZip: formData.get('billingZip'),
                      billingCountry: billingCountry,
                      isDefault: formData.get('isDefault') === 'on'
                    });
                  }}>
                    <div className="form-group">
                      <label>Cardholder Name</label>
                      <input 
                        type="text" 
                        name="cardholderName" 
                        className="form-control" 
                        placeholder="Name on card"
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label>Card Number</label>
                      <input 
                        type="text" 
                        name="cardNumber" 
                        className="form-control" 
                        placeholder="1234 5678 9012 3456"
                        pattern="[0-9]{13,19}"
                        maxLength={19}
                        required 
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group col-md-4">
                        <label>Expiry Month</label>
                        <select name="expiryMonth" className="form-control" required>
                          <option value="">Select Month</option>
                          {[...Array(12)].map((_, i) => (
                            <option key={i} value={String(i + 1).padStart(2, '0')}>
                              {String(i + 1).padStart(2, '0')}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group col-md-4">
                        <label>Expiry Year</label>
                        <select name="expiryYear" className="form-control" required>
                          <option value="">Select Year</option>
                          {[...Array(10)].map((_, i) => {
                            const year = new Date().getFullYear() + i;
                            return (
                              <option key={year} value={year}>{year}</option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="form-group col-md-4">
                        <label>CVV</label>
                        <input 
                          type="text" 
                          name="cvv" 
                          className="form-control" 
                          placeholder="123"
                          pattern="[0-9]{3,4}"
                          maxLength={4}
                          required 
                          title="3 or 4 digit security code"
                        />
                      </div>
                    </div>
                    
                    <h5 className="mt-3">Billing Address</h5>
                    <div className="form-group">
                      <label>Country</label>
                      <select 
                        name="billingCountry" 
                        className="form-control" 
                        value={billingCountry}
                        onChange={(e) => setBillingCountry(e.target.value as 'CA' | 'US')}
                        required
                      >
                        <option value="CA">Canada</option>
                        <option value="US">United States</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Street Address</label>
                      <input 
                        type="text" 
                        name="billingStreet" 
                        className="form-control" 
                        placeholder={billingCountry === 'CA' ? "123 King Street" : "123 Main Street"}
                        required 
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group col-md-6">
                        <label>City</label>
                        <input 
                          type="text" 
                          name="billingCity" 
                          className="form-control" 
                          placeholder={billingCountry === 'CA' ? "Toronto" : "New York"}
                          required 
                        />
                      </div>
                      <div className="form-group col-md-4">
                        <label>{billingCountry === 'CA' ? 'Province' : 'State'}</label>
                        <select name="billingState" className="form-control" required>
                          <option value="">Select {billingCountry === 'CA' ? 'Province' : 'State'}</option>
                          {billingCountry === 'CA' ? (
                            <>
                              <option value="AB">Alberta</option>
                              <option value="BC">British Columbia</option>
                              <option value="MB">Manitoba</option>
                              <option value="NB">New Brunswick</option>
                              <option value="NL">Newfoundland and Labrador</option>
                              <option value="NS">Nova Scotia</option>
                              <option value="NT">Northwest Territories</option>
                              <option value="NU">Nunavut</option>
                              <option value="ON">Ontario</option>
                              <option value="PE">Prince Edward Island</option>
                              <option value="QC">Quebec</option>
                              <option value="SK">Saskatchewan</option>
                              <option value="YT">Yukon</option>
                            </>
                          ) : (
                            <>
                              <option value="AL">Alabama</option>
                              <option value="AK">Alaska</option>
                              <option value="AZ">Arizona</option>
                              <option value="AR">Arkansas</option>
                              <option value="CA">California</option>
                              <option value="CO">Colorado</option>
                              <option value="CT">Connecticut</option>
                              <option value="DE">Delaware</option>
                              <option value="FL">Florida</option>
                              <option value="GA">Georgia</option>
                              <option value="HI">Hawaii</option>
                              <option value="ID">Idaho</option>
                              <option value="IL">Illinois</option>
                              <option value="IN">Indiana</option>
                              <option value="IA">Iowa</option>
                              <option value="KS">Kansas</option>
                              <option value="KY">Kentucky</option>
                              <option value="LA">Louisiana</option>
                              <option value="ME">Maine</option>
                              <option value="MD">Maryland</option>
                              <option value="MA">Massachusetts</option>
                              <option value="MI">Michigan</option>
                              <option value="MN">Minnesota</option>
                              <option value="MS">Mississippi</option>
                              <option value="MO">Missouri</option>
                              <option value="MT">Montana</option>
                              <option value="NE">Nebraska</option>
                              <option value="NV">Nevada</option>
                              <option value="NH">New Hampshire</option>
                              <option value="NJ">New Jersey</option>
                              <option value="NM">New Mexico</option>
                              <option value="NY">New York</option>
                              <option value="NC">North Carolina</option>
                              <option value="ND">North Dakota</option>
                              <option value="OH">Ohio</option>
                              <option value="OK">Oklahoma</option>
                              <option value="OR">Oregon</option>
                              <option value="PA">Pennsylvania</option>
                              <option value="RI">Rhode Island</option>
                              <option value="SC">South Carolina</option>
                              <option value="SD">South Dakota</option>
                              <option value="TN">Tennessee</option>
                              <option value="TX">Texas</option>
                              <option value="UT">Utah</option>
                              <option value="VT">Vermont</option>
                              <option value="VA">Virginia</option>
                              <option value="WA">Washington</option>
                              <option value="WV">West Virginia</option>
                              <option value="WI">Wisconsin</option>
                              <option value="WY">Wyoming</option>
                            </>
                          )}
                        </select>
                      </div>
                      <div className="form-group col-md-2">
                        <label>{billingCountry === 'CA' ? 'Postal Code' : 'ZIP Code'}</label>
                        <input 
                          type="text" 
                          name="billingZip" 
                          className="form-control" 
                          placeholder={billingCountry === 'CA' ? "M5V 3A8" : "10001"}
                          pattern={billingCountry === 'CA' ? "[A-Za-z]\\d[A-Za-z][ -]?\\d[A-Za-z]\\d" : "\\d{5}(-\\d{4})?"}
                          maxLength={billingCountry === 'CA' ? 7 : 10}
                          required 
                          title={billingCountry === 'CA' ? "Please enter a valid Canadian postal code (e.g., M5V 3A8)" : "Please enter a valid US ZIP code (e.g., 12345 or 12345-6789)"}
                        />
                      </div>
                    </div>
                    
                    <div className="form-check">
                      <input 
                        type="checkbox" 
                        name="isDefault" 
                        className="form-check-input" 
                        id="setAsDefault"
                      />
                      <label className="form-check-label" htmlFor="setAsDefault">
                        Set as default payment method
                      </label>
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn btn-success">
                        <i className="fas fa-save"></i> Add Card
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => setShowAddCard(false)}
                        style={{ marginLeft: '0.5rem' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
              
              {paymentLoading ? (
                <div className="text-center py-4">
                  <i className="fas fa-spinner fa-spin"></i> Loading payment methods...
                </div>
              ) : paymentMethods.length > 0 ? (
                <div className="payment-methods">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="payment-card">
                      <div className="card-type">
                        <i className={`fab fa-cc-${method.cardType.toLowerCase()}`}></i>
                      </div>
                      <div className="card-details">
                        <div className="card-number">
                          {method.cardType} ending in {method.lastFour}
                        </div>
                        <div className="card-expiry">
                          Expires {method.expiryMonth}/{method.expiryYear}
                        </div>
                      </div>
                      <div className="card-status">
                        {method.isDefault && (
                          <span className="status-badge status-paid">Default</span>
                        )}
                      </div>
                      <div className="card-actions">
                        {!method.isDefault && (
                          <button 
                            className="action-button"
                            onClick={() => handleSetDefault(method.id)}
                          >
                            Set Default
                          </button>
                        )}
                        <button 
                          className="action-button"
                          onClick={() => handleDeleteCard(method.id)}
                          disabled={method.isDefault}
                          title={method.isDefault ? "Cannot delete default payment method" : "Delete payment method"}
                        >
                          <i className="fas fa-trash-alt"></i> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-records">
                  <i className="fas fa-credit-card"></i>
                  <p>No payment methods on file. Add a card to enable automatic billing.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'autorecharge' && (
          <Suspense fallback={<div className="loading">Loading auto-recharge settings...</div>}>
            <AutoRecharge />
          </Suspense>
        )}

        {activeTab === 'sms' && (
          <Suspense fallback={<div className="loading">Loading SMS/MMS usage report...</div>}>
            <SMSUsageReport customerId={customer.id} />
          </Suspense>
        )}
      </div>
    </>
  );
};

export default Billing;